import React, { useState, useEffect } from "react";
import * as adminService from "@/shared/api/services/adminService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { auth } from "@/shared/api/graphqlClient";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Pencil,
  Trash2,
  Key,
  PowerOff,
  Power,
  Building2,
  Eye,
} from "lucide-react"; 
import { Link, useSearchParams } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import MerchantForm from "@/shared/components/shared/MerchantForm";
import EntityPanel from "@/shared/components/shared/EntityPanel";
import { EntityStatusBadge } from "@/shared/components/shared/EntityStatusBadge";

export default function Merchants() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const pageSize = 15;

  const queryClient = useQueryClient();

  const { data: merchants = [], isLoading, error } = useQuery({
    queryKey: ["merchants"],
    queryFn: () => adminService.getAllMerchants(),
    enabled: auth.isAuthenticated(),
    retry: 2,
    refetchOnMount: true,
    staleTime: 0,
  });

  const { data: engines = [] } = useQuery({
    queryKey: ["engines"],
    queryFn: () => adminService.getAllEngines(),
  });

  const { data: resellers = [] } = useQuery({
    queryKey: ["resellers"],
    queryFn: () => adminService.getAllResellers(),
  });

  const createMutation = useMutation({
    mutationFn: (data) => adminService.createMerchant(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["merchants"]);
      setFormOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => adminService.updateMerchant(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["merchants"]);
      setFormOpen(false);
      setSelectedMerchant(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => adminService.deleteMerchant(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["merchants"]);
      setDeleteDialog(null);
    },
  });

  const filteredMerchants = merchants.filter(
    (m) =>
      m.name?.toLowerCase().includes(search.toLowerCase()) ||
      m.code?.toLowerCase().includes(search.toLowerCase()) ||
      m.email?.toLowerCase().includes(search.toLowerCase())
  );

  const paginatedMerchants = filteredMerchants.slice(page * pageSize, (page + 1) * pageSize);
  const totalPages = Math.ceil(filteredMerchants.length / pageSize);

  const handleSave = async (data) => {
    if (selectedMerchant) {
      await updateMutation.mutateAsync({ id: selectedMerchant.id, data });
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  useEffect(() => {
    const editId = searchParams.get("editId");
    if (editId && merchants.length > 0) {
      const merchant = merchants.find((m) => m.id === editId);
      if (merchant) {
        setSelectedMerchant(merchant);
        setFormOpen(true);
      }
    }
  }, [searchParams, merchants]);

  const handleEdit = (merchant) => {
    setSelectedMerchant(merchant);
    setFormOpen(true);
  };

  const handleToggleStatus = async (merchant) => {
    const currentStatus = merchant.status?.toUpperCase();
    const newStatus = currentStatus === "ACTIVE" ? "SUSPENDED" : "ACTIVE";
    await updateMutation.mutateAsync({ id: merchant.id, data: { name: merchant.name, status: newStatus } });
  };

  const renderParentBadge = (merchant) => {
    if (merchant.createdByResellerId) {
      const reseller = resellers.find((r) => r.id === merchant.createdByResellerId);
      return (
        <Badge variant="outline" className="border-purple-300 text-purple-700 bg-purple-50 text-xs">
          Reseller: {reseller?.name || merchant.createdByResellerId.substring(0, 8)}
        </Badge>
      );
    }

    if (merchant.createdByEngineId) {
      const engine = engines.find((e) => e.id === merchant.createdByEngineId);
      return (
        <Badge variant="outline" className="border-sky-300 text-sky-700 bg-sky-50 text-xs">
          Engine: {engine?.name || merchant.createdByEngineId.substring(0, 8)}
        </Badge>
      );
    }

    if (merchant.createdByAdminId) {
      return (
        <Badge variant="outline" className="border-slate-300 text-slate-700 bg-slate-50 text-xs">
          {t("directAdmin")}
        </Badge>
      );
    }

    return <span className="text-xs text-slate-400">Unknown</span>;
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{t("merchants")}</h1>
          <p className="text-sm sm:text-base text-slate-500">
            {isLoading ? (
              <Skeleton width={150} />
            ) : (
              `${merchants.length} ${t("merchantsRegistered")}`
            )}
          </p>
        </div>
        <Button
          onClick={() => setFormOpen(true)}
          className="gap-2 w-full sm:w-auto hover:scale-105 transition-all duration-300 shadow-sm hover:bg-gradient-to-br hover:from-blue-500 hover:to-blue-700"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">{t("addMerchant")}</span>
          <span className="sm:hidden">{t("add")}</span>
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 text-red-700">
          <p className="font-medium text-sm sm:text-base">Error loading merchants</p>
          <p className="text-xs sm:text-sm">{error.message || "Failed to fetch merchants"}</p>
        </div>
      )}

      {/* Search */}
      <div className="relative w-full sm:max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder={t("searchMerchantsByIdEmailName") || t("searchMerchants")}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          className="pl-10"
        />
      </div>

      {/* Mobile Cards (Keep local as they are layout-specific) */}
      <div className="md:hidden space-y-3">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm border border-slate-100 p-4">
              <div className="flex items-center gap-3">
                <Skeleton circle height={40} width={40} />
                <div className="flex-1">
                  <Skeleton width="60%" />
                  <Skeleton width="40%" />
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <Skeleton height={20} />
                <Skeleton height={20} />
                <Skeleton height={20} />
                <Skeleton height={20} />
              </div>
            </div>
          ))
        ) : filteredMerchants.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 text-center text-slate-500">
            {merchants.length === 0 ? t("noMerchantsFound") : t("noMerchantsMatchSearch")}
          </div>
        ) : (
          paginatedMerchants.map((merchant, index) => (
            <motion.div
              key={merchant.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.02 * index }}
              className="bg-white rounded-xl shadow-sm border border-slate-100 p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 truncate">{merchant.name}</p>
                    <p className="text-sm text-slate-500 truncate">{merchant.email || t("noEmail")}</p>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem asChild>
                      <Link to={createPageUrl("AdminMerchantDetail") + `?id=${merchant.id}`}>
                        <Eye className="h-4 w-4 mr-2" />
                        {t("viewDetails")}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleEdit(merchant)}>
                      <Pencil className="h-4 w-4 mr-2" />
                      {t("edit")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleEdit(merchant)}>
                      <Key className="h-4 w-4 mr-2" />
                      {t("viewApiKeys")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => handleToggleStatus(merchant)}>
                      {merchant.status?.toUpperCase() === "ACTIVE" ? (
                        <>
                          <PowerOff className="h-4 w-4 mr-2" />
                          {t("suspend")}
                        </>
                      ) : (
                        <>
                          <Power className="h-4 w-4 mr-2" />
                          {t("activate")}
                        </>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setDeleteDialog(merchant)}
                      className="text-red-600"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {t("delete")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-slate-500">{t("code")}</p>
                  <Badge variant="outline" className="font-mono text-xs mt-1">
                    {merchant.code || "N/A"}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-slate-500">{t("status")}</p>
                  <div className="mt-1">
                    <EntityStatusBadge status={merchant.status} t={t} />
                  </div>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-slate-500">{t("parent")}</p>
                  <div className="mt-1">{renderParentBadge(merchant)}</div>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Desktop/Tablet Table */}
      <div className="hidden md:block">
        <EntityPanel
          entityType="merchant"
          data={paginatedMerchants}
          isLoading={isLoading}
          t={t}
          resellers={resellers}
          engines={engines}
          onEdit={handleEdit}
          onToggleStatus={handleToggleStatus}
          onDelete={setDeleteDialog}
        />
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs sm:text-sm text-slate-500 text-center sm:text-left">
            {t("showing")} {page * pageSize + 1} {t("to")} {Math.min((page + 1) * pageSize, filteredMerchants.length)} {t("of")} {filteredMerchants.length} {t("merchants")}
          </p>
          <div className="flex items-center justify-center gap-2">
            <Button variant="outline" size="sm" className="h-9 w-9 p-0" onClick={() => setPage(page - 1)} disabled={page === 0}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-xs sm:text-sm text-slate-600">{t("page")} {page + 1} {t("of")} {totalPages}</span>
            <Button variant="outline" size="sm" className="h-9 w-9 p-0" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Merchant Form Dialog */}
      <MerchantForm
        merchant={selectedMerchant}
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setSelectedMerchant(null);
        }}
        onSave={handleSave}
        engines={engines}
        resellers={resellers}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteMerchant")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("deleteConfirmation")} "{deleteDialog?.name}"? {t("cannotBeUndone")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate(deleteDialog.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}